import { useEffect, useState } from "react";
import { INSTRUCTION_IMAGE_LIMITS, type InstructionImage, type InstructionImageContentType } from "@probetalab/contracts";
import { get, run, urlFor } from "../lib/api.js";
import { useSession } from "../lib/session.js";
import { blobToBase64 } from "../lib/signature.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { type Line, StudySearch } from "./StudyPicker.js";
import { Alert, Button, Card, Chip, Empty, Field, Input, Loading, Sheet, Switch } from "./ui.js";

/**
 * Configuración → Indicaciones para pacientes (migration 0019, pilot request 2026-11-02): the
 * pictures the counter sends with a WhatsApp quote ("Recolección de orina"…). The Administrador
 * uploads a PNG or JPG up to 2 MB, names it and links it to the studies it applies to; a quote
 * with any of those studies ticks it by default. An image is never edited or deleted — it is
 * retired, and a new one uploaded.
 */

/** Why a file cannot be uploaded, in es-MX, or null. Checked before reading it, the API re-checks. */
export function instructionFileProblem(file: { size: number; type: string }): string | null {
  if (file.type !== "image/png" && file.type !== "image/jpeg") return "La imagen debe ser PNG o JPG.";
  if (file.size === 0) return "La imagen está vacía.";
  if (file.size > INSTRUCTION_IMAGE_LIMITS.maxBytes) return "La imagen pesa más de 2 MB. Redúcela e inténtalo de nuevo.";
  return null;
}

/** "recoleccion_de_orina.jpg" → "Recoleccion de orina". */
export function nameFromFilename(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return (base.charAt(0).toLocaleUpperCase("es-MX") + base.slice(1)).slice(0, INSTRUCTION_IMAGE_LIMITS.maxNameLength);
}

export function InstructionImagesCard() {
  const { isOperator } = useSession();
  const images = useQuery(() => get("instructionImages", { query: { include_inactive: true } }), []);
  const catalogue = useQuery(() => get("catalogue"), []);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<InstructionImage | null>(null);
  const studyName = (id: string) => {
    const s = catalogue.data?.studies.find((x) => x.id === id);
    return s ? `${s.clave} · ${s.name}` : "…";
  };

  return (
    <Card
      title="Indicaciones para pacientes"
      actions={
        isOperator ? undefined : (
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            Agregar imagen
          </Button>
        )
      }
    >
      <p className="muted small" style={{ marginTop: 0 }}>
        Imágenes que se envían por WhatsApp con una cotización. Si la ligas a estudios, se marca sola cuando la cotización incluye alguno.
      </p>
      {images.loading && !images.data ? (
        <Loading />
      ) : !images.data?.items.length ? (
        <Empty icon="doc" title="Sin imágenes" hint="Agrega, por ejemplo, cómo recolectar la muestra de orina." />
      ) : (
        <div className="stack">
          {images.data.items.map((img) => (
            <div key={img.id} className="row" style={{ gap: 14, alignItems: "flex-start", opacity: img.is_active ? 1 : 0.55 }}>
              <img src={urlFor("instructionImageFile", { params: { id: img.id } })} alt={img.name} width={72} height={72} style={{ objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />
              <div className="grow stack" style={{ gap: 6 }}>
                <strong>{img.name}</strong>
                <span className="row wrap" style={{ gap: 6 }}>
                  {img.study_ids.length ? img.study_ids.map((id) => <Chip key={id}>{studyName(id)}</Chip>) : <span className="small faint">Sin estudios ligados</span>}
                  {!img.is_active && <Chip tone="amber">Retirada</Chip>}
                </span>
              </div>
              {!isOperator && (
                <Button size="sm" variant="ghost" icon="pen" onClick={() => setEditing(img)}>
                  Editar
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {creating && <ImageSheet open onClose={() => setCreating(false)} onSaved={() => void images.reload()} studyName={studyName} />}
      {editing && <ImageSheet open image={editing} onClose={() => setEditing(null)} onSaved={() => void images.reload()} studyName={studyName} />}
    </Card>
  );
}

function ImageSheet({
  open,
  image,
  onClose,
  onSaved,
  studyName,
}: {
  open: boolean;
  image?: InstructionImage | null;
  onClose: () => void;
  onSaved: () => void;
  studyName: (id: string) => string;
}) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [studyIds, setStudyIds] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setFileError(null);
    setName(image?.name ?? "");
    setStudyIds(image?.study_ids ?? []);
    setActive(image?.is_active ?? true);
  }, [open, image]);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function pick(f: File | undefined) {
    if (!f) return;
    const problem = instructionFileProblem(f);
    setFileError(problem);
    if (problem) {
      setFile(null);
      return;
    }
    setFile(f);
    if (!name.trim()) setName(nameFromFilename(f.name));
  }

  async function save() {
    setBusy(true);
    try {
      if (image) {
        await run("instruction_image.update", { image_id: image.id, name: name.trim(), study_ids: studyIds, is_active: active });
        toast.success("Imagen actualizada.");
      } else {
        await run("instruction_image.create", {
          name: name.trim(),
          content_type: file!.type as InstructionImageContentType,
          image_base64: await blobToBase64(file!),
          study_ids: studyIds,
        });
        toast.success("Imagen agregada.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const canSave = name.trim().length > 0 && (image ? true : !!file);
  const shown = preview ?? (image ? urlFor("instructionImageFile", { params: { id: image.id } }) : null);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={image ? "Editar imagen de indicaciones" : "Agregar imagen de indicaciones"}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!canSave} onClick={() => void save()}>
            Guardar
          </Button>
        </>
      }
    >
      {shown && <img src={shown} alt="Vista previa" style={{ maxWidth: "100%", maxHeight: 260, objectFit: "contain", borderRadius: 8, border: "1px solid var(--line)" }} />}
      {!image && (
        <div className="stack" style={{ gap: 6 }}>
          <label className="btn btn-secondary" style={{ justifySelf: "start", position: "relative" }}>
            {file ? "Cambiar imagen" : "Elegir imagen"}
            <input
              type="file"
              accept="image/png,image/jpeg"
              aria-label="Imagen de indicaciones"
              className="sr-only"
              onChange={(e) => {
                pick(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
          <span className="small muted">PNG o JPG, hasta 2 MB. La imagen no se puede cambiar después: para otra versión, agrega una nueva y retira esta.</span>
          {fileError && <Alert tone="red">{fileError}</Alert>}
        </div>
      )}
      <Field label="Nombre">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={INSTRUCTION_IMAGE_LIMITS.maxNameLength} placeholder="Recolección de orina" />
      </Field>
      <div className="field">
        <span className="label">
          Estudios a los que aplica <span className="opt">(opcional)</span>
        </span>
        <div className="stack">
          {studyIds.length > 0 && (
            <span className="row wrap" style={{ gap: 6 }}>
              {studyIds.map((id) => (
                <button key={id} type="button" className="chip" onClick={() => setStudyIds((s) => s.filter((x) => x !== id))} aria-label={`Quitar ${studyName(id)}`}>
                  {studyName(id)} ×
                </button>
              ))}
            </span>
          )}
          <StudySearch studiesOnly selected={studyIds.map((id) => ({ kind: "study", id }) as Line)} onPick={(it) => setStudyIds((s) => (s.includes(it.id) ? s : [...s, it.id]))} />
        </div>
        <span className="hint">Si la cotización incluye alguno, la imagen se marca sola.</span>
      </div>
      {image && (
        <Switch checked={active} onChange={setActive}>
          Disponible para enviar
        </Switch>
      )}
    </Sheet>
  );
}
